/**
 * R-167 — token cheatsheet slide-out shared by the tenant-level
 * `DisplayTemplatesSection` (R-146) and the per-JobType
 * `JobTypeDisplayTemplatesSection`. Lists every token from
 * `TOKEN_CATALOG` grouped by category. Clicking a row inserts the
 * token into the parent's currently-focused template input via
 * `onInsert`.
 *
 * Pure presentational — the parent owns the "which input is focused"
 * + "append token to current value" logic.
 */
import { TOKEN_CATALOG } from "@capture/shared";

export interface TokenCatalogPanelProps {
  onInsert: (token: string) => void;
  onClose: () => void;
}

const GROUPS = ["identity", "date", "counter", "source", "field", "position"] as const;

const GROUP_LABEL: Record<(typeof GROUPS)[number], string> = {
  identity: "Identity",
  date: "Date",
  counter: "Counter",
  source: "Source",
  field: "Field",
  position: "Position",
};

const panelStyle: React.CSSProperties = {
  position: "fixed",
  right: 0,
  top: 0,
  bottom: 0,
  width: 360,
  background: "var(--color-bg-secondary)",
  borderLeft: "1px solid var(--color-border)",
  padding: 16,
  overflowY: "auto",
  zIndex: 100,
  boxShadow: "var(--shadow-lg, 0 4px 12px rgba(0,0,0,0.4))",
  color: "var(--color-text-primary)",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 12,
};

const closeBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--color-border)",
  borderRadius: 4,
  padding: "4px 10px",
  color: "var(--color-text-primary)",
  cursor: "pointer",
  fontSize: 14,
};

const introStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-muted)",
  marginBottom: 12,
};

const groupHeaderStyle: React.CSSProperties = {
  textTransform: "uppercase",
  fontSize: 11,
  color: "var(--color-text-muted)",
  letterSpacing: "0.05em",
  margin: "8px 0 4px",
};

const tokenButtonStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: 8,
  margin: "4px 0",
  background: "var(--color-bg-primary)",
  border: "1px solid var(--color-border)",
  borderRadius: 4,
  cursor: "pointer",
  color: "var(--color-text-primary)",
  fontFamily: "inherit",
};

const tokenLabelStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono, monospace)",
  fontWeight: 600,
};

const tokenDescStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  marginTop: 2,
};

const tokenExampleStyle: React.CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-muted)",
  marginTop: 2,
};

export default function TokenCatalogPanel({ onInsert, onClose }: TokenCatalogPanelProps) {
  return (
    <aside style={panelStyle} aria-label="Token cheatsheet">
      <header style={headerStyle}>
        <h3 style={{ margin: 0, fontSize: 16, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Token cheatsheet
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close cheatsheet"
          style={closeBtnStyle}
        >
          ✕
        </button>
      </header>
      <p style={introStyle}>
        Click a token to insert it into the focused template input. Static text
        (anything outside <code>{`{}`}</code>) is preserved verbatim.
      </p>
      {GROUPS.map((g) => {
        const items = TOKEN_CATALOG.filter((t) => t.group === g);
        if (items.length === 0) return null;
        return (
          <section key={g} style={{ marginBottom: 16 }}>
            <h4 style={groupHeaderStyle}>{GROUP_LABEL[g]}</h4>
            {items.map((t) => (
              <button
                key={t.token}
                type="button"
                onClick={() => onInsert(t.token)}
                style={tokenButtonStyle}
              >
                <code style={tokenLabelStyle}>{t.token}</code>
                <div style={tokenDescStyle}>{t.description}</div>
                <div style={tokenExampleStyle}>
                  e.g. <code>{t.example}</code>
                </div>
              </button>
            ))}
          </section>
        );
      })}
    </aside>
  );
}
